import { ENTITY_SLUGS, ListingsStateProvider, useListingStore } from 'modules/Listings/store/StateContext';
import { withListLayout } from 'modules/Listings/withListLayout';
import { useCallback, useEffect, useMemo } from 'react';
import { BreadcrumbJsonLd } from 'next-seo';
import { GetServerSideProps } from 'next';
import { useQueryClient } from 'react-query';
import { createModelsList } from 'modules/Models/listingStore/createModelsList';
import { useRouter } from 'next/router';
import { modelsListType } from 'modules/Listings/store/Store.types';
import { ListView } from 'modules/Listings/components/ListView/ListView';
import { buildCommunityBreadcrumbs } from 'utils/breadcrumbs';
import { getDefautServerProps } from 'server/getDefaultServerProps';
import { FILTER_ID } from 'modules/Listings/components/AllStarTemplateToggle';

const ModelsPage: React.FC<{
  host: string;
}> = ({ host }) => {
  const router = useRouter();
  const query = router.query as Record<string, string>;
  const queryClient = useQueryClient();

  const modelsStore = useListingStore<modelsListType>(
    useCallback(
      () =>
        createModelsList({
          hasFilters: true,
          queryClient,
          userIdFromRouter: undefined,
          isStarred: query?.activeToggle === FILTER_ID.STARRED,
          initialQuery: query,
        }),
      [],
    ),
    [],
  );

  useEffect(() => {
    if (!modelsStore.isInitialisingFilters) {
      modelsStore.load();
    }
  }, [modelsStore, modelsStore.isInitialisingFilters]);

  useEffect(() => {
    if (query.searchQuery) {
      modelsStore.setSearchTermValue(query.searchQuery);
      modelsStore.load();
    }
  }, [modelsStore, query?.searchQuery]);

  // instantiating inside component so ListingsStateProvider can give state to search, filters etc.
  const ListWithLayout = useMemo(() => withListLayout(ListView, true), []);

  return (
    <ListingsStateProvider store={modelsStore} entityType={ENTITY_SLUGS.models}>
      <BreadcrumbJsonLd itemListElements={buildCommunityBreadcrumbs({ host, page: 'models' })} />
      <ListWithLayout
        pageName="models_page"
        title="Explore state-of-the-art AI models"
        heading="Jumpstart your AI solutions with pre-built models from Clarifai, leading AI providers and community contributors."
        requestLinkText="Request Model"
      />
    </ListingsStateProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, req, res, query }) => {
  const host = req ? req.headers.host : 'clarifai.com';
  const defautServerProps = await getDefautServerProps({ params, req, res });
  return {
    props: {
      ...defautServerProps,
      host,
      initialQuery: query,
      trackEventProps: {
        eventName: 'Community Models Page Viewed',
      },
      seoProps: {
        title: 'Explore state-of-the-art AI models',
        description: 'Jumpstart your AI solutions with pre-built models from Clarifai, leading AI providers and community contributors.',
      },
    },
  };
};

export default ModelsPage;
